// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AutocompleteItemModel,
    AutocompleteModel,
    ProductAutocompleteItemModel,
} from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FancyScroll, { FancyScrollRefProps } from "@synd/App/Components/Common/FancyScroll";
import { FloatingTooltipStyles } from "@synd/App/Components/FloatingTooltip/FloatingTooltip";
import FloatingMenuTooltip from "@synd/App/Components/Menu/FloatingMenuTooltip";
import { CustomAppState } from "@synd/App/Store/Reducers";
import TextField, { TextFieldPresentationProps } from "@synd/MobiusOverrides/TextField";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import AutocompleteProducts, { AutocompleteProductsStyles } from "@synd/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@synd/Overrides/Widgets/Header/SearchHistory";
import { parseBoolean } from "@synd/XDL/Utilities";
import breakpointMediaQueries from "@synd/XDL/Utilities/breakpointMediaQueries";
import { produce } from "immer";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
        isMenuOpened: (state as CustomAppState).xCommon.isMenuOpened,
        isInsufficientInventoryModalOpened: state.xCommon.isInsufficientInventoryModal?.isOpen,
        isPreBookModalOpen: state.xCommon.isPreBookModalOpen,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    // XNG-Changes
    loadRealTimePricing,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    // autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;
    // XNG-Changes
    autoCompleteModel?: AutocompleteModel & { productContexts: ProductContextModel[] };
    autoCompleteOpen?: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteProductsColumn?: InjectableCss;
    autocompleteColumnProductWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    searchPopUpWrapper?: InjectableCss;
    floatingTooltip?: FloatingTooltipStyles;
    fancyScroll?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

// XNG-Changes
let autoCompleteRequestTimestamp: number;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private autocompleteLeftColumn = React.createRef<HTMLDivElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef1 = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
            // XNG-Changes
            autoCompleteOpen: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                this.setState({ query: "" });

                // XNG-Changes
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
            } else {
                this.closeAutocomplete();
            }
        }
        // XNG-Changes
        if (
            (this.props.isMenuOpened ||
                this.props.isInsufficientInventoryModalOpened ||
                this.props.isPreBookModalOpen) &&
            this.state.autoCompleteOpen
        ) {
            this.closeAutocomplete();
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            // XNG-Changes
            this.goToUrl(this.state.autoCompleteModel?.products[0].url, true, 1);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
        // XNG-Change close if there is no history
        const searchHistoryItems = getSearchHistory();
        const hasSearchHistory = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        if (!hasSearchHistory) {
            this.closeAutocomplete();
        }
    };

    updateAutoCompleteProductInfo = ({ productId, newValue }: { productId: string; newValue: string | number }) => {
        this.setState(
            (prevState: any) => {
                const productContexts = prevState.autoCompleteModel?.productContexts.map((productState: any) => {
                    if (productState.product.id === productId) {
                        productState.productInfo.qtyOrdered = newValue;
                    }
                    return productState;
                });
                return {
                    ...prevState,
                    autoCompleteModel: {
                        ...prevState.autoCompleteModel,
                        productContexts: cloneDeep(productContexts),
                    },
                };
            },
            () => {
                this.fancyScrollRef1?.current?.updateScrollable();
            },
        );
    };

    createProductContext = (product: ProductAutocompleteItemModel, query: string) => {
        const productInfo: any = {
            productId: product.id!,
            productDetailPath: product.url,
            qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
            unitOfMeasure: product.properties.unitOfMeasure,
            pricing: product.properties.pricing,
            failedToLoadPricing: product.properties.failedToLoadPricing,
            inventory: product.properties.inventory,
            failedToLoadInventory: product.properties.failedToLoadInventory,
        };

        const productModel: any = {
            id: product.id!,
            productNumber: product.erpNumber,
            customerProductNumber: product.name,
            mediumImagePath: product.image,
            productTitle: product.title,
            canonicalUrl: product.url,
            uri: product.uri,
            canAddToCart: parseBoolean(product.properties.canAddToCart),
            manufacturerItem: product.manufacturerItemNumber,
            minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
            isVariantParent: parseBoolean(product.properties.isVariantParent),
            trackInventory: parseBoolean(product.properties.trackInventory),
            packDescription: product.properties?.packDescription,
            brand: {
                name: product.brandName,
                detailPagePath: product.brandDetailPagePath,
            },
            properties: {
                ...product.properties,
            } as any,
            unitOfMeasures: [
                {
                    unitOfMeasure: product.properties.unitOfMeasure,
                    unitOfMeasureDisplay: product.properties.unitOfMeasure,
                },
            ],
        };

        const updateProductInfo = (value: string | number) => {
            this.updateAutoCompleteProductInfo({
                productId: product.id!,
                newValue: value,
            });
        };

        const newProduct: ProductContextModel = {
            product: { ...productModel },
            productInfo,
            onQtyOrderedChanged: qty => updateProductInfo(qty),
        };

        return newProduct;
    };

    // XNG-Changes
    loadAutocomplete = debounce((query: string) => {
        // XNG-Changes
        this.setState({ isLoading: true });
        const currentRequestTimestamp = (autoCompleteRequestTimestamp = new Date().getTime());
        this.props.getAutocompleteModel({
            query,
            onSuccess: (result: any) => {
                if (currentRequestTimestamp !== autoCompleteRequestTimestamp) {
                    return;
                }
                let autoCompleteResult: AutocompleteModel = {} as AutocompleteModel;
                if (this.showAutocomplete) {
                    autoCompleteResult = {
                        ...result,
                        productContexts: (result?.products || []).map((product: any) =>
                            this.createProductContext(product, query),
                        ),
                    };
                    this.setState(
                        {
                            autoCompleteModel: autoCompleteResult as any,
                        },
                        () => {
                            this.fancyScrollRef1?.current?.updateScrollable();
                        },
                    );
                    // <XNG-Changes>
                    this.updatePopoverVisibility(autoCompleteResult);

                    // </XNG-Changes>
                    // this.openAutocomplete();
                }
                // XNG-Changes
                this.setState({ isLoading: false });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        // XNG-Changes
        if (this.state.query) {
            this.loadAutocomplete(newQuery);
        }

        // XNG-Changes
        if (newQuery === "") {
            this.clearSearchQuery();
        }
    };

    // XNG-Changes
    handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        this.showAutocomplete = true;
        const pastedData = event.clipboardData.getData("text");
        this.loadAutocomplete(pastedData);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.setState({ autoCompleteOpen: true });
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.setState({ autoCompleteOpen: false });
    };

    // XNG-Changes
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    // XNG-Changes
    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        // if (!this.state.query) {
        //     this.showAutocomplete = true;
        // } else {
        //     this.showAutocomplete = false;
        // }
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    // XNG-Changes: update type +
    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    // XNG-Changes: add autoCompleteResultLength
    goToUrl = (url: string, track = true, autoCompleteResultLength?: number) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        // XNG-Changes: only when clicking/hitting enter on pdp product from search result
        //  (having 1 product search result only) remove stale autocomplete results
        if (autoCompleteResultLength && autoCompleteResultLength === 1) {
            this.setState({
                // don't use stale results on next search and go to wrong PDP
                autoCompleteModel: undefined,
            });
        }
        this.props.history.push(url);
    };

    // XNG-Changes
    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
            // XNG-Changes
            productContexts,
        } = this.state.autoCompleteModel;

        // XNG-Changes
        const hasProducts = products && products.length > 0;
        const hasContent =
            (categories && categories.length > 0) || (brands && brands.length > 0) || (content && content.length > 0);
        const inputWidth = this.inputWrapperRef.current?.getBoundingClientRect().width;
        return (
            <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper" {...{ inputWidth }}>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}

                {/* XNG-Changes */}
                {!isRetailSearchCompletionResults && (
                    <>
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <FancyScroll
                                refElement={this.autocompleteLeftColumn}
                                ref={this.fancyScrollRef1}
                                extendedStyles={styles.fancyScroll}
                            >
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnWrapper}
                                    className="autocomplete-column-wrapper"
                                    ref={this.autocompleteLeftColumn}
                                >
                                    <AutocompleteCategories
                                        categories={categories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    />
                                    <AutocompleteBrands
                                        brands={brands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                    />
                                    <AutocompleteContent
                                        content={content}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteContentStyles}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        )}
                        {hasProducts && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnProductWrapper}
                                // XNG-Changes
                                className="autocomplete-column-wrapper"
                                {...{ hasContent }}
                            >
                                <AutocompleteProducts
                                    // XNG-Changes
                                    products={productContexts}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteProductsStyles}
                                    // XNG-Changes
                                    {...{ doSearch: this.doSearch, canSeePrices: this.props.canSeePrices }}
                                />
                            </StyledWrapper>
                        )}
                    </>
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        const searchHistoryItems = getSearchHistory();
        const hasSearchHistory = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        // XNG-Changes: add hasSearchHistory
        if (!this.state.query && this.showAutocomplete && hasSearchHistory) {
            return (
                <StyledWrapper {...styles.searchPopUpWrapper}>
                    <SearchHistory
                        focusedQuery={this.state.focusedSearchHistoryQuery}
                        goToUrl={this.goToUrl}
                        // XNG-Changes
                        clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                    />
                </StyledWrapper>
            );
        }

        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };

    // XNG-Changes
    render() {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;
        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    placeholder={translate("Search")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    // XNG-Changes
                    maxLength={100}
                    border="rounded"
                    aria-multiline="false"
                    aria-required="false"
                    onKeyDown={this.handlePopoverKeyDown}
                    onFocus={this.handleInputFocus}
                    onPaste={this.handlePaste}
                    clearClickableProps={{
                        clickableProps: { onClick: () => this.clearSearchQuery() },
                    }}
                />
            </StyledWrapper>
        );
        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: replace Popover with FloatingMenu */}
                {/* 
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                    className="popover"
                >
                    {!this.state.query && this.showAutocomplete && (
                        <SearchHistory
                            focusedQuery={this.state.focusedSearchHistoryQuery}
                            goToUrl={this.goToUrl}
                            extendedStyles={styles.searchHistoryStyles}
                        />
                    )}
                    {this.state.query && this.renderAutocomplete()}
                </Popover>
                */}
                <FloatingMenuTooltip
                    trigger="manual"
                    isOpen={this.state.autoCompleteOpen}
                    onOpenChange={open => (open ? this.handleInputFocus() : this.closeAutocomplete())}
                    triggerComponent={searchInput}
                    overlay={true}
                    showArrow={false}
                    placement={"bottom-start"}
                    offset={this.props.isTabletScreen ? 16 : 0}
                    shift={{
                        padding: 0,
                    }}
                    extendedStyles={styles.floatingTooltip}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
